import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Badge,
  Alert,
  AlertDescription,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Progress
} from '../../components/ui';
import {
  Utensils,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Loader2,
  FileText,
  Package,
  Download,
  Clock,
  CheckCircle2,
  AlertCircle,
  Settings,
  RefreshCw,
  FileStack,
  Trash2,
  X,
  Info,
  Eye,
  Building,
  Hash,
  Tag,
  FileBarChart
} from 'lucide-react';
import { gapi } from 'gapi-script';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { saveAs } from 'file-saver';

const CLIENT_ID = '513169446517-8t6mm1hc3q2dlfiit3k9n04k4op6s3ih.apps.googleusercontent.com';
const API_KEY = 'AIzaSyDj6U0S0fY66gxnqv6P_VR3LiEcpgvzDzk';
const SPREADSHEET_ID = '1ok2BQYB6FbXXVBVs4uBWRZhMPoMN47k9-Hkj57YYSyE';
const SHEET_NAME = 'Requests';
const CLIENT_INFO_SHEET_NAME = 'Client Information';
const SCOPES = 'https://www.googleapis.com/auth/spreadsheets';

// FDA API Configuration
const FDA_API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:3001'
  : 'https://fda-prior-notice-submission-simulator-production.up.railway.app';

// US State code to full name mapping
const US_STATES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

// Note: Submitter info is now loaded dynamically from FDA_CLIENT_INFO database based on team ID

const FDASection = ({ shipmentId, shipmentItems, selectedItems, onItemToggle, onComplete, clientsData, shipmentInfo, boxes, selectedPlan }) => {
  // FDA submissions are tied to the outbound shipment ID from the URL (e.g., 369237)
  // This ensures robust tracking across browser sessions and page reloads
  
  // Google Sheets related state
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [uniqueAsins, setUniqueAsins] = useState(new Set());
  
  // Submission method and general state
  const [submissionMethod, setSubmissionMethod] = useState('api'); // 'sheets' or 'api'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmedPlacementOption, setConfirmedPlacementOption] = useState(null);
  
  // API submission state
  const [apiSubmissions, setApiSubmissions] = useState([]);
  const [pollingIntervals, setPollingIntervals] = useState(new Map());
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  
  // Dynamic FDA client info state
  const [submitterInfo, setSubmitterInfo] = useState(null);
  const [isLoadingSubmitterInfo, setIsLoadingSubmitterInfo] = useState(false);
  const [submitterInfoError, setSubmitterInfoError] = useState(null);
  
  // Helper function to check if multiple FDA items are in the same box
  const getBoxItemConflicts = () => {
    if (!selectedItems.length || !boxes.length) return { hasConflicts: false, conflictDetails: [] };
    
    const boxItemCounts = new Map();
    const conflictDetails = [];
    
    selectedItems.forEach(item => {
      if (!item) return;
      
      boxes.forEach(box => {
        const matchingBoxItem = box.items.find(boxItem => {
          return (boxItem.sku || boxItem.SKU) === item.item.merchant_sku;
        });
        
        if (matchingBoxItem) {
          const boxNumber = box.box_number;
          if (!boxItemCounts.has(boxNumber)) {
            boxItemCounts.set(boxNumber, []);
          }
          boxItemCounts.get(boxNumber).push({
            asin: item.item.asin,
            title: item.item.title || item.item.TITLE,
            boxNumber: boxNumber
          });
        }
      });
    });
    
    // Find boxes with multiple FDA items
    boxItemCounts.forEach((items, boxNumber) => {
      if (items.length > 1) {
        conflictDetails.push({
          boxNumber: boxNumber,
          itemCount: items.length,
          items: items
        });
      }
    });
    
    return {
      hasConflicts: conflictDetails.length > 0,
      conflictDetails: conflictDetails
    };
  };

  // API URL from environment
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3005';

  // Initialize Google API
  useEffect(() => {
    const initClient = () => {
      gapi.client.init({
        apiKey: API_KEY,
        clientId: CLIENT_ID,
        scope: SCOPES,
        discoveryDocs: ['https://sheets.googleapis.com/$discovery/rest?version=v4'],
      }).then(() => {
        const authInstance = gapi.auth2.getAuthInstance();
        authInstance.isSignedIn.listen(updateSigninStatus);
        updateSigninStatus(authInstance.isSignedIn.get());
      });
    };

    gapi.load('client:auth2', initClient);
  }, []);

  // Check placement options when selectedPlan changes
  useEffect(() => {
    if (selectedPlan) {
      checkPlacementOptions(selectedPlan);
    }
  }, [selectedPlan]);

  // Load FDA submissions and client info on mount
  useEffect(() => {
    if (shipmentId) {
      loadFDASubmissions();
    }
    if (shipmentInfo?.team_id) {
      loadFDAClientInfo();
    }
  }, [shipmentId, shipmentInfo?.team_id]);

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      pollingIntervals.forEach(interval => clearInterval(interval));
    };
  }, [pollingIntervals]);

  const updateSigninStatus = (isSignedIn) => {
    setIsAuthorized(isSignedIn);
    if (isSignedIn) {
      readExistingAsins();
    }
  };

  const handleAuthClick = () => {
    gapi.auth2.getAuthInstance().signIn();
  };

  const readExistingAsins = async () => {
    try {
      const response = await gapi.client.sheets.spreadsheets.values.get({
        spreadsheetId: SPREADSHEET_ID,
        range: SHEET_NAME,
      });

      const data = response.result;
      if (data.values && data.values.length > 0) {
        const headers = data.values[0];
        const asinColumnIndex = headers.findIndex(header => header === 'ASIN OR SKU');
        
        if (asinColumnIndex !== -1) {
          const asins = new Set();
          data.values.slice(1).forEach(row => {
            if (row[asinColumnIndex]) {
              asins.add(row[asinColumnIndex]);
            }
          });
          setUniqueAsins(asins);
        }
      }
    } catch (error) {
      console.error('Error reading existing ASINs:', error);
    }
  };

  // Load FDA client info based on team ID
  const loadFDAClientInfo = async () => {
    if (!shipmentInfo?.team_id) {
      console.log('No team ID available for FDA client info');
      return;
    }

    console.log(`Loading FDA client info for team ID: ${shipmentInfo.team_id}`);
    setIsLoadingSubmitterInfo(true);
    setSubmitterInfoError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/fda-client-info/${shipmentInfo.team_id}`);
      
      if (response.ok) {
        const clientInfo = await response.json();
        
        // Map to the expected submitter info format
        const mappedSubmitterInfo = {
          businessName: clientInfo.companyName,
          firstName: clientInfo.customerName.split(' ')[0] || clientInfo.customerName,
          lastName: clientInfo.customerName.split(' ').slice(1).join(' ') || '',
          streetAddress: clientInfo.address1,
          city: clientInfo.city,
          state: clientInfo.province,
          zipCode: clientInfo.postalCode,
          country: clientInfo.country,
          countryCode: clientInfo.country === 'CA' ? 'CA' : 'US',
          phoneNumber: '9043858588', // Default phone - you may want to add this to the database
          email: 'hello@yyzprep.ca' // Default email - you may want to add this to the database
        };
        
        setSubmitterInfo(mappedSubmitterInfo);
        console.log('FDA client info loaded successfully:', mappedSubmitterInfo);
        
      } else if (response.status === 404) {
        const errorData = await response.json();
        setSubmitterInfoError(errorData.message);
        console.error('FDA client info not found:', errorData.message);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
    } catch (error) {
      console.error('Error loading FDA client info:', error);
      setSubmitterInfoError(`Failed to load FDA client information: ${error.message}`);
    } finally {
      setIsLoadingSubmitterInfo(false);
    }
  };

  // Database functions
  const loadFDASubmissions = async () => {
    if (!shipmentId) return;

    console.log(`Loading FDA submissions for outbound shipment ID: ${shipmentId}`);
    setIsLoadingSubmissions(true);
    try {
      const response = await fetch(`${API_URL}/api/fda-submissions/${shipmentId}`);
      if (response.ok) {
        const submissions = await response.json();
        
        // Convert database format to component format and start polling for active submissions
        const formattedSubmissions = submissions.map(sub => {
          const formattedSubmission = {
            id: sub.job_id,
            createdAt: sub.created_at,
            status: sub.status,
            progress: sub.progress || 0,
            totalSubmissions: sub.total_submissions,
            completedSubmissions: sub.completed_submissions || 0,
            failedSubmissions: sub.failed_submissions || 0,
            statusEndpoint: sub.status_endpoint,
            resultsEndpoint: sub.results_endpoint,
            estimatedCompletionTime: sub.estimated_completion_time,
            updatedAt: sub.updated_at,
            submissions: sub.individual_submissions?.map(ind => ({
              id: ind.submission_id,
              index: ind.submission_index,
              status: ind.status,
              asin: ind.asin,
              quantity: ind.quantity,
              box_number: ind.box_number, // Include box number from database
              startTime: ind.start_time,
              endTime: ind.end_time,
              duration: ind.duration_ms,
              error: ind.error_message,
              result: {
                pdfBase64: ind.pdf_base64, // This will be null initially
                pdfStatus: ind.pdf_status, // 'available' if PDF exists
                productInfo: ind.product_info ? (() => {
                  if (typeof ind.product_info === 'string') {
                    try {
                      return JSON.parse(ind.product_info);
                    } catch (e) {
                      console.warn('Invalid JSON in product_info:', ind.product_info);
                      return null;
                    }
                  }
                  return ind.product_info;
                })() : null
              }
            })) || []
          };

          // Check current status before starting polling for pending/processing submissions
          if (sub.status === 'pending' || sub.status === 'processing') {
            // First check current status to avoid unnecessary polling
            checkAndStartPolling(sub.job_id, sub.status_endpoint);
          }

          return formattedSubmission;
        });

        setApiSubmissions(formattedSubmissions);
      }
    } catch (error) {
      console.error('Error loading FDA submissions:', error);
    } finally {
      setIsLoadingSubmissions(false);
    }
  };

  const saveFDASubmission = async (submissionRecord, individualSubmissions) => {
    try {
      const response = await fetch(`${API_URL}/api/fda-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jobId: submissionRecord.id,
          shipmentId: shipmentId,
          teamId: shipmentInfo?.team_id,
          totalSubmissions: submissionRecord.totalSubmissions,
          statusEndpoint: submissionRecord.statusEndpoint,
          resultsEndpoint: submissionRecord.resultsEndpoint,
          estimatedCompletionTime: submissionRecord.estimatedCompletionTime,
          submissionData: submissionRecord.submissions,
          individualSubmissions: individualSubmissions,
          createdBy: 'web-interface'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save FDA submission to database');
      }

      console.log('FDA submission saved to database successfully');
    } catch (error) {
      console.error('Error saving FDA submission to database:', error);
      // Don't throw error - let the submission continue even if database save fails
    }
  };

  const updateFDASubmissionInDatabase = async (jobId, statusData, individualSubmissions) => {
    try {
      const response = await fetch(`${API_URL}/api/fda-submissions/${jobId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: statusData.status,
          progress: statusData.progress,
          completedSubmissions: statusData.completedSubmissions,
          failedSubmissions: statusData.failedSubmissions,
          resultData: statusData,
          individualSubmissions: individualSubmissions
        }),
      });

      if (!response.ok) {
        console.error('Failed to update FDA submission in database');
      }
    } catch (error) {
      console.error('Error updating FDA submission in database:', error);
    }
  };

  // API submission functions
  const handleApiSubmission = async () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one food item');
      return;
    }

    // Check if submitter info is loaded
    if (!submitterInfo) {
      if (submitterInfoError) {
        alert(`Cannot submit FDA prior notice: ${submitterInfoError}`);
      } else if (isLoadingSubmitterInfo) {
        alert('Please wait for submitter information to load before submitting.');
      } else {
        alert('Submitter information is required but not available. Please ensure the client is configured in the FDA database.');
      }
      return;
    }

    setIsSubmitting(true);
    try {
      // Group selected items by box using new API format
      const boxSubmissions = new Map();

      selectedItems.forEach(item => {
        if (!item) return;

        boxes.forEach(box => {
          const shipment = findShipmentForBox(box);
          
          const matchingBoxItem = box.items.find(boxItem => {
            return (boxItem.sku || boxItem.SKU) === item.item.merchant_sku;
          });

          if (shipment && matchingBoxItem) {
            const boxNumber = box.box_number;
            const quantity = matchingBoxItem.quantity || matchingBoxItem.QTY || 0;
            const stateCode = shipment.destination.address.state_or_province_code;
            const fullStateName = US_STATES[stateCode] || stateCode;
            
            if (!boxSubmissions.has(boxNumber)) {
              boxSubmissions.set(boxNumber, {
                boxId: `BOX-${boxNumber}`,
                submitter: {
                  businessName: submitterInfo.businessName,
                  firstName: submitterInfo.firstName,
                  lastName: submitterInfo.lastName,
                  email: submitterInfo.email,
                  phoneNumber: submitterInfo.phoneNumber,
                  streetAddress: submitterInfo.streetAddress,
                  city: submitterInfo.city,
                  state: submitterInfo.state,
                  zipCode: submitterInfo.zipCode,
                  countryCode: submitterInfo.countryCode
                },
                recipient: {
                  businessName: "Amazon FBA Warehouse",
                  streetAddress: shipment.destination.address.address_line_1,
                  city: shipment.destination.address.city,
                  state: fullStateName,
                  zipCode: shipment.destination.address.postal_code,
                  countryCode: "US"
                },
                products: []
              });
            }
            
            boxSubmissions.get(boxNumber).products.push({
              asin: item.item.asin,
              quantity: quantity
            });
          }
        });
      });

      if (boxSubmissions.size === 0) {
        throw new Error('No valid box submissions were generated. Please check that items exist in boxes.');
      }

      // Convert to new API format
      const requestPayload = {
        boxes: Array.from(boxSubmissions.values()),
        options: {
          priority: "normal",
          notificationEmail: submitterInfo.email
        }
      };

      console.log('Submitting to FDA API with payload:', requestPayload);

      // Submit to new FDA API endpoint
      const response = await fetch(`${FDA_API_BASE_URL}/api/fda/submit-boxes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`API request failed (${response.status}): ${errorData.error?.message || response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error?.message || 'FDA submission failed');
      }
      
      // Create submission record with new format
      const submissionRecord = {
        id: result.jobId,
        createdAt: result.timestamp,
        status: result.status,
        totalSubmissions: result.summary.totalBoxes,
        completedSubmissions: 0,
        failedSubmissions: 0,
        progress: result.progress,
        submissions: [],
        statusEndpoint: result.statusUrl,
        resultsEndpoint: result.resultsUrl,
        estimatedCompletionTime: result.estimatedTimeMinutes ? 
          new Date(Date.now() + result.estimatedTimeMinutes * 60000).toISOString() : null
      };

      // Save to database with individual submission details
      const individualSubmissionDetails = Array.from(boxSubmissions.values()).flatMap(box => 
        box.products.map(product => ({
          asin: product.asin,
          quantity: product.quantity,
          boxNumber: box.boxId.replace('BOX-', '')
        }))
      );

      console.log(`Saving FDA submission for outbound shipment ID: ${shipmentId}`);
      await saveFDASubmission(submissionRecord, individualSubmissionDetails);

      // Add to submissions list
      setApiSubmissions(prev => [submissionRecord, ...prev]);

      // Start polling for this submission
      startPolling(submissionRecord.id, result.statusUrl);

      setSubmissionResult({
        success: true,
        jobId: result.jobId,
        count: result.summary.totalProducts,
        method: 'api'
      });
      setDialogOpen(true);

    } catch (error) {
      console.error('Error submitting to FDA API:', error);
      setSubmissionResult({
        success: false,
        error: error.message,
        method: 'api'
      });
      setDialogOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check current status before starting polling to avoid unnecessary polling
  const checkAndStartPolling = async (jobId, statusEndpoint) => {
    try {
      console.log(`Checking current status for job ${jobId} before starting polling`);
      const response = await fetch(`${FDA_API_BASE_URL}${statusEndpoint}`);
      
      if (!response.ok) {
        console.error('Failed to check job status:', response.status);
        // If we can't check status, start polling as fallback
        startPolling(jobId, statusEndpoint);
        return;
      }

      const statusData = await response.json();
      
      if (!statusData.success) {
        console.error('API returned error:', statusData.error);
        // If we can't get valid status, start polling as fallback
        startPolling(jobId, statusEndpoint);
        return;
      }

      console.log(`Current status for ${jobId}:`, statusData.status);

      // Update state immediately with current status
      setApiSubmissions(prev => prev.map(submission => {
        if (submission.id === jobId) {
          const updatedSubmissions = statusData.boxes?.map(box => {
            const existingSub = submission.submissions?.find(s => 
              s.boxId === box.boxId || s.box_number === box.boxId?.replace('BOX-', '')
            );
            
            return {
              id: box.boxId,
              boxId: box.boxId,
              box_number: box.boxId?.replace('BOX-', ''),
              status: box.status,
              progress: box.progress,
              productCount: box.productCount,
              startedAt: box.startedAt,
              completedAt: box.completedAt,
              hasResult: box.hasResult,
              hasError: box.hasError,
              asin: existingSub?.asin || (box.productCount === 1 ? 'Single Product' : 'Multiple Products'),
              result: existingSub?.result || {
                pdfBase64: null,
                pdfStatus: box.hasResult ? 'available' : 'not_available'
              }
            };
          }) || submission.submissions;

          return {
            ...submission,
            status: statusData.status,
            progress: statusData.progress,
            completedSubmissions: statusData.summary?.completedBoxes || 0,
            failedSubmissions: statusData.summary?.failedBoxes || 0,
            totalSubmissions: statusData.summary?.totalBoxes || submission.totalSubmissions,
            updatedAt: statusData.timestamp,
            submissions: updatedSubmissions
          };
        }
        return submission;
      }));

      // Update database with current status
      await updateFDASubmissionInDatabase(jobId, statusData, statusData.boxes || []);

      // Only start polling if job is actually still in progress
      if (statusData.status === 'pending' || statusData.status === 'processing') {
        console.log(`Job ${jobId} is still ${statusData.status}, starting polling`);
        startPolling(jobId, statusEndpoint);
      } else if (statusData.status === 'completed') {
        console.log(`Job ${jobId} is already completed, fetching results`);
        fetchJobResults(jobId);
      } else {
        console.log(`Job ${jobId} has final status: ${statusData.status}`);
      }

    } catch (error) {
      console.error('Error checking job status:', error);
      // If we can't check status, start polling as fallback
      startPolling(jobId, statusEndpoint);
    }
  };

  // Polling function with new API format
  const startPolling = (jobId, statusEndpoint) => {
    // Check if already polling this job
    if (pollingIntervals.has(jobId)) {
      console.log(`Already polling job ${jobId}, skipping duplicate`);
      return;
    }

    console.log(`Starting polling for job ${jobId}`);
    const startTime = Date.now();
    const maxPollingDuration = 30 * 60 * 1000; // 30 minutes max polling
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollInterval = setInterval(async () => {
      try {
        // Check if we've been polling too long
        if (Date.now() - startTime > maxPollingDuration) {
          console.log(`Polling timeout reached for job ${jobId}, stopping`);
          clearInterval(pollInterval);
          setPollingIntervals(prev => {
            const newMap = new Map(prev);
            newMap.delete(jobId);
            return newMap;
          });
          return;
        }

        // Use the full statusEndpoint path provided by the API
        const response = await fetch(`${FDA_API_BASE_URL}${statusEndpoint}`);
        
        if (!response.ok) {
          consecutiveErrors++;
          console.error(`Polling failed (${consecutiveErrors}/${maxConsecutiveErrors}):`, response.status);
          
          if (consecutiveErrors >= maxConsecutiveErrors) {
            console.error(`Too many consecutive errors for job ${jobId}, stopping polling`);
            clearInterval(pollInterval);
            setPollingIntervals(prev => {
              const newMap = new Map(prev);
              newMap.delete(jobId);
              return newMap;
            });
          }
          return;
        }

        const statusData = await response.json();
        
        if (!statusData.success) {
          consecutiveErrors++;
          console.error(`API returned error (${consecutiveErrors}/${maxConsecutiveErrors}):`, statusData.error);
          
          if (consecutiveErrors >= maxConsecutiveErrors) {
            console.error(`Too many consecutive errors for job ${jobId}, stopping polling`);
            clearInterval(pollInterval);
            setPollingIntervals(prev => {
              const newMap = new Map(prev);
              newMap.delete(jobId);
              return newMap;
            });
          }
          return;
        }

        // Reset error counter on successful response
        consecutiveErrors = 0;
        
        console.log(`Polling update for ${jobId}:`, {
          status: statusData.status,
          progress: statusData.progress,
          boxesCount: statusData.boxes?.length || 0,
          completedBoxes: statusData.summary?.completedBoxes || 0,
          totalBoxes: statusData.summary?.totalBoxes || 0
        });
        
        // Update submission record with new format
        setApiSubmissions(prev => prev.map(submission => {
          if (submission.id === jobId) {
            // Map boxes to submissions format for compatibility
            const updatedSubmissions = statusData.boxes?.map(box => {
              // Find existing submission to preserve data
              const existingSub = submission.submissions?.find(s => 
                s.boxId === box.boxId || s.box_number === box.boxId?.replace('BOX-', '')
              );
              
              return {
                id: box.boxId,
                boxId: box.boxId,
                box_number: box.boxId?.replace('BOX-', ''),
                status: box.status,
                progress: box.progress,
                productCount: box.productCount,
                startedAt: box.startedAt,
                completedAt: box.completedAt,
                hasResult: box.hasResult,
                hasError: box.hasError,
                asin: existingSub?.asin || (box.productCount === 1 ? 'Single Product' : 'Multiple Products'),
                result: existingSub?.result || {
                  pdfBase64: null,
                  pdfStatus: box.hasResult ? 'available' : 'not_available'
                }
              };
            }) || submission.submissions;

            return {
              ...submission,
              status: statusData.status,
              progress: statusData.progress,
              completedSubmissions: statusData.summary?.completedBoxes || 0,
              failedSubmissions: statusData.summary?.failedBoxes || 0,
              totalSubmissions: statusData.summary?.totalBoxes || submission.totalSubmissions,
              updatedAt: statusData.timestamp,
              submissions: updatedSubmissions
            };
          }
          return submission;
        }));

        // Update database with latest status
        await updateFDASubmissionInDatabase(jobId, statusData, statusData.boxes || []);

        // Stop polling if completed
        if (statusData.status === 'completed' || statusData.status === 'failed') {
          clearInterval(pollInterval);
          setPollingIntervals(prev => {
            const newMap = new Map(prev);
            newMap.delete(jobId);
            return newMap;
          });
          
          // If completed, fetch results to get PDF data
          if (statusData.status === 'completed') {
            fetchJobResults(jobId);
          }
        }

      } catch (error) {
        consecutiveErrors++;
        console.error(`Error polling status (${consecutiveErrors}/${maxConsecutiveErrors}):`, error);
        
        if (consecutiveErrors >= maxConsecutiveErrors) {
          console.error(`Too many consecutive errors for job ${jobId}, stopping polling`);
          clearInterval(pollInterval);
          setPollingIntervals(prev => {
            const newMap = new Map(prev);
            newMap.delete(jobId);
            return newMap;
          });
        }
      }
    }, 5000); // Poll every 5 seconds

    setPollingIntervals(prev => new Map(prev).set(jobId, pollInterval));
  };

  // Fetch job results when completed
  const fetchJobResults = async (jobId) => {
    try {
      const response = await fetch(`${FDA_API_BASE_URL}/api/fda/jobs/${jobId}/results`);
      
      if (!response.ok) {
        console.error('Failed to fetch job results:', response.status);
        return;
      }

      const resultsData = await response.json();
      
      if (!resultsData.success) {
        console.error('Results API returned error:', resultsData.error);
        return;
      }

      console.log('Job results fetched successfully:', {
        jobId,
        completedBoxes: resultsData.boxes?.filter(b => b.status === 'completed').length || 0,
        totalBoxes: resultsData.boxes?.length || 0
      });

      // Update submissions with PDF data
      setApiSubmissions(prev => prev.map(submission => {
        if (submission.id === jobId) {
          const updatedSubmissions = resultsData.boxes?.map(box => {
            const existingSub = submission.submissions?.find(s => 
              s.boxId === box.boxId || s.box_number === box.boxId?.replace('BOX-', '')
            );

            return {
              ...existingSub,
              id: box.boxId,
              boxId: box.boxId,
              box_number: box.boxId?.replace('BOX-', ''),
              status: box.status,
              error: box.error,
              result: box.result ? {
                pdfBase64: box.result.documents?.pdfBase64,
                pdfUrl: box.result.documents?.pdfUrl,
                pdfStatus: box.result.documents?.pdfBase64 ? 'available' : 'not_available',
                fdaSubmissionId: box.result.fdaSubmissionId,
                confirmationNumber: box.result.documents?.confirmationNumber,
                productInfo: box.result.products
              } : existingSub?.result
            };
          }) || submission.submissions;

          return {
            ...submission,
            submissions: updatedSubmissions
          };
        }
        return submission;
      }));

      // Update database with PDF data
      await updateFDASubmissionInDatabase(jobId, resultsData, resultsData.boxes || []);

    } catch (error) {
      console.error('Error fetching job results:', error);
    }
  };

  // Download PDF function with new API endpoints
  const downloadPDF = async (submissionData) => {
    try {
      let pdfBase64 = submissionData.result?.pdfBase64;
      
      // If PDF data isn't loaded but status shows available, try direct download endpoint
      if (!pdfBase64 && submissionData.result?.pdfStatus === 'available') {
        try {
          // Extract job ID from submission data
          const jobId = submissionData.id?.includes('JOB-') ? 
            submissionData.id.split('-').slice(1, 4).join('-') : 
            submissionData.id;
          const boxId = submissionData.boxId || submissionData.id;
          
          // Try the new PDF download endpoint
          const pdfResponse = await fetch(`${FDA_API_BASE_URL}/api/fda/jobs/${jobId}/boxes/${boxId}/pdf`);
          
          if (pdfResponse.ok) {
            const pdfBlob = await pdfResponse.blob();
            const url = URL.createObjectURL(pdfBlob);
            
            const a = document.createElement('a');
            a.href = url;
            const boxNumber = submissionData.boxNumber || submissionData.box_number || boxId?.replace('BOX-', '');
            const fileName = `PN_${selectedPlan?.inbound_plan_id || selectedPlan?.id || 'UNKNOWN'}.pdf`;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            return;
          }
        } catch (error) {
          console.warn(`Failed to fetch PDF via direct endpoint for ${submissionData.id}:`, error);
        }
        
        // Fallback: try to get PDF from results endpoint
        try {
          const jobId = submissionData.id?.includes('JOB-') ? 
            submissionData.id.split('-').slice(1, 4).join('-') : 
            submissionData.id;
          
          const resultsResponse = await fetch(`${FDA_API_BASE_URL}/api/fda/jobs/${jobId}/results`);
          if (resultsResponse.ok) {
            const resultsData = await resultsResponse.json();
            if (resultsData.success) {
              const targetBox = resultsData.boxes?.find(b => 
                b.boxId === submissionData.boxId || 
                b.boxId === submissionData.id
              );
              
              if (targetBox?.result?.documents?.pdfBase64) {
                pdfBase64 = targetBox.result.documents.pdfBase64;
                console.log(`Retrieved PDF data from results endpoint for ${submissionData.id}, size: ${pdfBase64.length}`);
              }
            }
          }
        } catch (error) {
          console.warn(`Failed to fetch PDF from results endpoint for ${submissionData.id}:`, error);
        }
      }
      
      if (!pdfBase64) {
        alert(`PDF data not available for submission ${submissionData.id}. The PDF may still be processing or failed to generate.`);
        return;
      }
      
      const binaryString = atob(pdfBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      const boxNumber = submissionData.boxNumber || submissionData.box_number;
      const fileName = `PN_${selectedPlan?.inbound_plan_id || selectedPlan?.id || 'UNKNOWN'}.pdf`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF. Please try again.');
    }
  };

  // State for tracking PDF processing
  const [processingPdf, setProcessingPdf] = useState(null);
  
  // State for deletion
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // State for ASIN breakdown view
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [submissionForBreakdown, setSubmissionForBreakdown] = useState(null);
  const [isLoadingBreakdown, setIsLoadingBreakdown] = useState(false);

  // Download merged, stamped and converted to 4x6 PDF
  const downloadMergedStamped4x6PDF = async (submission) => {
    const jobId = submission.job_id || submission.id;
    setProcessingPdf(jobId);

    try {
      // Get all completed submissions with available PDFs from the correct data structure
      const submissionData = submission.submissions || submission.individual_submissions || [];
      const completedSubmissions = submissionData
        .filter(sub => sub.status === 'completed' && (sub.result?.pdfBase64 || sub.result?.pdfStatus === 'available'))
        .sort((a, b) => (a.box_number || a.boxNumber || 1) - (b.box_number || b.boxNumber || 1)) // Sort by box number first
        .map((sub, index) => {
          // Use same logic as individual download buttons for consistent box numbering
          const boxNumber = sub.box_number || sub.boxNumber || (index + 1);
          console.log(`Mapping submission ${sub.id}: box_number=${sub.box_number}, boxNumber=${sub.boxNumber}, mapped to=${boxNumber}`);
          
          return {
            ...sub,
            boxNumber: boxNumber
          };
        });

      if (completedSubmissions.length === 0) {
        alert('No completed PDFs found to merge and convert.');
        return;
      }

      console.log(`Creating merged 4x6 PDF with ${completedSubmissions.length} submissions`);

      // Create merged PDF document
      const mergedPdf = await PDFDocument.create();
      const helveticaFont = await mergedPdf.embedFont(StandardFonts.Helvetica);

      for (const sub of completedSubmissions) {
        try {
          console.log(`Processing box ${sub.boxNumber} with ASIN ${sub.asin}`);
          
          let pdfBase64 = sub.result?.pdfBase64;
          
          // If no PDF data but status shows available, try to get from FDA service
          if (!pdfBase64 && sub.result?.pdfStatus === 'available') {
            try {
              const batchId = sub.id.split('-').slice(0, 3).join('-'); // Extract batch ID from submission ID
              const statusResponse = await fetch(`${FDA_API_BASE_URL}/api/batch-status/${batchId}`);
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                const targetSubmission = statusData.submissions?.find(s => s.id === sub.id);
                if (targetSubmission?.result?.pdfBase64) {
                  pdfBase64 = targetSubmission.result.pdfBase64;
                  console.log(`Fetched PDF data from FDA service for ${sub.id}`);
                }
              }
            } catch (fetchError) {
              console.warn(`Failed to fetch PDF from FDA service for ${sub.id}:`, fetchError);
            }
          }
          
          if (!pdfBase64) {
            console.warn(`No PDF data available for submission ${sub.id} (Box ${sub.boxNumber}). Status: ${sub.status}, pdfStatus: ${sub.result?.pdfStatus}`);
            continue; // Skip this submission
          }

          // Convert base64 to Uint8Array
          const pdfBytes = Uint8Array.from(atob(pdfBase64), c => c.charCodeAt(0));
          
          // Load the individual PDF
          const individualPdf = await PDFDocument.load(pdfBytes);
          const pages = await mergedPdf.copyPages(individualPdf, individualPdf.getPageIndices());
          
          // Add pages and stamp each one with the box number
          pages.forEach((page) => {
            // Add the page first
            mergedPdf.addPage(page);
            
            // Get page dimensions for centering
            const { width, height } = page.getSize();
            
            // Get last 4 characters of inbound plan ID for stamp
            const planCode = selectedPlan?.inbound_plan_id ? 
              selectedPlan.inbound_plan_id.slice(-4) : 'XXXX';
            
            // Stamp with plan code and box number (centered on page)
            page.drawText(`${planCode}-BOX ${sub.boxNumber}`, {
              x: width / 2 - 50, // Adjust centering for longer text
              y: height / 2 + 5, // 5 pixels higher
              size: 18,          // Smaller font (was 24)
              font: helveticaFont,
              color: rgb(0, 0, 0), // Black color
              opacity: 1.0,      // Fully opaque (was 0.3)
            });
          });
          
        } catch (error) {
          console.error(`Error processing submission ${sub.id}:`, error);
          continue; // Skip this submission and continue with others
        }
      }

      if (mergedPdf.getPageCount() === 0) {
        alert('No valid PDFs could be processed for merging and conversion.');
        return;
      }

      // Generate the merged PDF bytes
      const mergedPdfBytes = await mergedPdf.save();
      
      // Convert to base64 for the 4x6 conversion service (process in chunks to avoid stack overflow)
      const uint8Array = new Uint8Array(mergedPdfBytes);
      let binaryString = '';
      const chunkSize = 8192; // Process in 8KB chunks
      
      for (let i = 0; i < uint8Array.length; i += chunkSize) {
        const chunk = uint8Array.slice(i, i + chunkSize);
        binaryString += String.fromCharCode.apply(null, chunk);
      }
      
      const base64Data = btoa(binaryString);
      
      // Prepare payload for 4x6 conversion
      const planCode = selectedPlan?.inbound_plan_id ? 
        selectedPlan.inbound_plan_id.slice(-4) : 'XXXX';
      const payload = {
        name: `FDA_Prior_Notice_Merged_${planCode}_Boxes_${completedSubmissions[0].boxNumber}-${completedSubmissions[completedSubmissions.length - 1].boxNumber}.pdf`,
        type: 'application/pdf',
        base64Data: base64Data,
        sizeBytes: mergedPdfBytes.length,
      };

      console.log('Converting merged PDF to 4x6 format...');

      // Send to 4x6 conversion service
      const response = await fetch(`${API_URL}/proxy-retool`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        const convertedBase64PDF = result.response;

        // Convert base64 PDF back to Blob and download it
        const byteCharacters = atob(convertedBase64PDF);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const planCode = selectedPlan?.inbound_plan_id ? 
          selectedPlan.inbound_plan_id.slice(-4) : 'XXXX';
        link.href = url;
        link.download = `PN_${selectedPlan?.inbound_plan_id || selectedPlan?.id || 'UNKNOWN'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        URL.revokeObjectURL(url);
        console.log('Merged 4x6 PDF downloaded successfully');
      } else {
        console.error('Error converting PDF to 4x6:', response.statusText);
        alert('Failed to convert PDF to 4x6 format. Please try again.');
      }
      
    } catch (error) {
      console.error('Error creating merged 4x6 PDF:', error);
      alert('Failed to create merged 4x6 PDF: ' + error.message);
    } finally {
      setProcessingPdf(null);
    }
  };

  // Download merged stamped PDF function
  const downloadMergedStampedPDF = async (submission) => {
    const jobId = submission.job_id || submission.id;
    setProcessingPdf(jobId);

    try {
      // Get all completed submissions with available PDFs from the correct data structure
      const submissionData = submission.submissions || submission.individual_submissions || [];
      
      console.log('Raw submission data for merging:', {
        submissionsCount: submissionData.length,
        sampleSubmission: submissionData[0] ? {
          id: submissionData[0].id,
          status: submissionData[0].status,
          hasResult: !!submissionData[0].result,
          resultKeys: submissionData[0].result ? Object.keys(submissionData[0].result) : 'no result',
          hasPdfBase64: !!submissionData[0].result?.pdfBase64,
          pdfSize: submissionData[0].result?.pdfBase64?.length || 0,
          boxNumber: submissionData[0].box_number || submissionData[0].boxNumber
        } : 'none'
      });
      
      const completedSubmissions = submissionData
        .filter(sub => sub.status === 'completed' && (sub.result?.pdfBase64 || sub.result?.pdfStatus === 'available'))
        .sort((a, b) => (a.box_number || a.boxNumber || 1) - (b.box_number || b.boxNumber || 1)) // Sort first like individual buttons
        .map((sub, index) => {
          // Use same logic as individual download buttons that work correctly
          const boxNumber = sub.box_number || sub.boxNumber || (index + 1);
          console.log(`Mapping submission ${sub.id}: box_number=${sub.box_number}, boxNumber=${sub.boxNumber}, mapped to=${boxNumber}`);
          
          return {
            ...sub,
            boxNumber: boxNumber
          };
        }); // Remove second sort since we sorted before mapping

      console.log('Filtered completed submissions:', {
        totalSubmissions: submissionData.length,
        completedSubmissions: submissionData.filter(sub => sub.status === 'completed').length,
        completedWithPdf: completedSubmissions.length,
        completedSample: completedSubmissions[0] ? {
          id: completedSubmissions[0].id,
          boxNumber: completedSubmissions[0].boxNumber,
          hasPdfBase64: !!completedSubmissions[0].result?.pdfBase64,
          pdfSize: completedSubmissions[0].result?.pdfBase64?.length || 0
        } : 'none'
      });

      if (completedSubmissions.length === 0) {
        alert('No completed PDFs found to merge.');
        return;
      }

      console.log(`Creating merged PDF with ${completedSubmissions.length} submissions`);

      // Create a new merged PDF document
      const mergedPdf = await PDFDocument.create();
      const font = await mergedPdf.embedFont(StandardFonts.HelveticaBold);

      for (const sub of completedSubmissions) {
        try {
          console.log(`Processing box ${sub.boxNumber} with ASIN ${sub.asin}`);
          console.log(`Submission data:`, {
            id: sub.id,
            status: sub.status,
            hasResult: !!sub.result,
            hasPdfBase64: !!sub.result?.pdfBase64,
            pdfStatus: sub.result?.pdfStatus,
            resultKeys: sub.result ? Object.keys(sub.result) : 'no result'
          });
          
          let pdfBase64 = sub.result?.pdfBase64;
          
          // If no PDF data but status shows available, try to get from FDA service
          if (!pdfBase64 && sub.result?.pdfStatus === 'available') {
            try {
              const batchId = sub.id.split('-').slice(0, 3).join('-'); // Extract batch ID from submission ID
              const statusResponse = await fetch(`${FDA_API_BASE_URL}/api/batch-status/${batchId}`);
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                const targetSubmission = statusData.submissions?.find(s => s.id === sub.id);
                if (targetSubmission?.result?.pdfBase64) {
                  pdfBase64 = targetSubmission.result.pdfBase64;
                  console.log(`Retrieved PDF data from FDA service for ${sub.id}, size: ${pdfBase64.length}`);
                }
              }
            } catch (error) {
              console.warn(`Failed to fetch PDF from FDA service for ${sub.id}:`, error);
            }
          }
          
          if (!pdfBase64) {
            console.warn(`No PDF data available for submission ${sub.id} (Box ${sub.boxNumber}). Status: ${sub.status}, pdfStatus: ${sub.result?.pdfStatus}`);
            continue; // Skip this submission
          }
          
          // Convert base64 to bytes
          const binaryString = atob(pdfBase64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          // Load the PDF
          const pdfDoc = await PDFDocument.load(bytes);
          const pages = pdfDoc.getPages();

          // Copy all pages from this PDF
          const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
          
          for (const page of copiedPages) {
            // Get page dimensions
            const { width, height } = page.getSize();
            
            // Get last 4 characters of inbound plan ID for stamp
            const planCode = selectedPlan?.inbound_plan_id ? 
              selectedPlan.inbound_plan_id.slice(-4) : 'XXXX';
            
            // Calculate center position for plan code and box number stamp
            const stampText = `${planCode}-BOX ${sub.boxNumber}`;
            const fontSize = 24;
            const textWidth = font.widthOfTextAtSize(stampText, fontSize);
            const xPosition = (width - textWidth) / 2; // Center horizontally
            const yPosition = height / 2; // Center vertically

            // Stamp the plan code and box number on the page (centered)
            page.drawText(stampText, {
              x: xPosition,
              y: yPosition,
              size: fontSize,
              font: font,
              color: rgb(0, 0, 0),
              opacity: 0.3 // Semi-transparent so it doesn't interfere with content
            });

            // Add the stamped page to merged PDF
            mergedPdf.addPage(page);
          }
        } catch (pageError) {
          console.error(`Error processing PDF for box ${sub.boxNumber}:`, pageError);
          // Continue with other PDFs even if one fails
        }
      }

      // Save and download the merged PDF
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const jobId = submission.job_id || submission.id;
      const planCode = selectedPlan?.inbound_plan_id ? 
        selectedPlan.inbound_plan_id.slice(-4) : 'XXXX';
      saveAs(blob, `PN_${selectedPlan?.inbound_plan_id || selectedPlan?.id || 'UNKNOWN'}.pdf`);

      console.log('Merged stamped PDF download complete');
    } catch (error) {
      console.error('Error creating merged stamped PDF:', error);
      alert('Error creating merged PDF. Please try again.');
    } finally {
      setProcessingPdf(null);
    }
  };

  const getClientId = async (clientName) => {
    try {
      const response = await gapi.client.sheets.spreadsheets.values.get({
        spreadsheetId: SPREADSHEET_ID,
        range: CLIENT_INFO_SHEET_NAME,
      });

      const data = response.result;
      if (data.values && data.values.length > 0) {
        const headers = data.values[0];
        const rows = data.values.slice(1);

        const parsedData = rows.map(row => {
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = row[index] || '';
          });
          return rowData;
        });

        const clientInfo = parsedData.find(client => client['Company Name'] === clientName);
        return clientInfo ? clientInfo['Client ID'] : null;
      }
    } catch (error) {
      console.error('Error fetching client info:', error);
      return null;
    }
  };

  const checkPlacementOptions = (plan) => {
    if (!plan || !plan.placement_options || plan.placement_options.length === 0) {
      console.log('No placement options available.');
      return;
    }

    const confirmedOptions = plan.placement_options.filter(option => option.confirmed_at);
    if (confirmedOptions.length === 0) {
      console.log('No placement option has been confirmed.');
      return;
    }

    const latestConfirmedOption = confirmedOptions.reduce((latest, current) => {
      return new Date(current.confirmed_at) > new Date(latest.confirmed_at) ? current : latest;
    });

    setConfirmedPlacementOption(latestConfirmedOption);
  };

  const findShipmentForBox = (box) => {
    if (!confirmedPlacementOption || !confirmedPlacementOption.shipments) {
      console.log('No confirmed placement option or shipments found.');
      return null;
    }

    const boxNumber = box.boxNumber || box.box_number || box.number;
    const boxNumberInt = parseInt(boxNumber, 10);
    if (isNaN(boxNumberInt)) {
      console.error(`Invalid boxNumber: ${boxNumber} (tried boxNumber: ${box.boxNumber}, box_number: ${box.box_number}, number: ${box.number})`);
      return null;
    }

    for (const shipment of confirmedPlacementOption.shipments) {
      console.log(`Checking shipment ID: ${shipment.id}`);
      
      // First try to find the box in box_groups directly
      for (const boxGroup of shipment.box_groups) {
        if (Array.isArray(boxGroup.box_numbers) && boxGroup.box_numbers.length > 0) {
          if (boxGroup.box_numbers.includes(boxNumberInt)) {
            console.log(`Box number ${boxNumberInt} found in shipment ID: ${shipment.id}`);
            return shipment;
          }
        }
      }
      
      // If not found and box_numbers arrays are empty, check shipment.boxes
      if (shipment.boxes && Array.isArray(shipment.boxes)) {
        const matchingBox = shipment.boxes.find(b => b.box_number === boxNumberInt);
        if (matchingBox) {
          console.log(`Found matching box in shipment.boxes with box_group_id: ${matchingBox.box_group_id}`);
          
          // Find the corresponding box_group using the box_group_id
          const matchingBoxGroup = shipment.box_groups.find(bg => bg.id === matchingBox.box_group_id);
          if (matchingBoxGroup) {
            console.log(`Found matching box group in shipment`);
            return shipment;
          }
        }
      }
    }

    console.log(`No matching shipment found for box number: ${boxNumberInt}`);
    return null;
  };

  const handleFDASubmission = async () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one food item');
      return;
    }

    setIsSubmitting(true);
    try {
      // Get client data from the context
      const clientData = clientsData?.find(client => client.id === shipmentInfo?.team_id);
      if (!clientData) {
        throw new Error('Client data not found.');
      }
      
      const clientId = await getClientId(clientData.name);
      if (!clientId) {
        throw new Error(`Client ID not found for "${clientData.name}". Please add client to Google Sheets first.`);
      }

      const rows = [];
      const now = new Date();
      const dateRequested = now.toISOString();
      const dateRequestedTime = '';

      console.log("DEBUG - Selected Items:", selectedItems);
      console.log("DEBUG - Boxes:", boxes);
      console.log("DEBUG - Selected Plan:", selectedPlan);

      selectedItems.forEach(item => {
        console.log("DEBUG - Processing Item:", item);
        
        if (!item) {
          console.log("DEBUG - Item not found!");
          return;
        }

        boxes.forEach(box => {
          console.log("DEBUG - Processing Box:", box);
          console.log("DEBUG - Box Number Property:", box.boxNumber, "or box.box_number:", box.box_number, "or box.number:", box.number);
          const shipment = findShipmentForBox(box);
          console.log("DEBUG - Found Shipment:", shipment);
          
          const matchingBoxItem = box.items.find(boxItem => {
            console.log("DEBUG - Comparing SKUs:");
            console.log("Box Item SKU:", boxItem.sku || boxItem.SKU);
            console.log("Merchant SKU:", item.item.merchant_sku);
            return (boxItem.sku || boxItem.SKU) === item.item.merchant_sku;
          });
          console.log("DEBUG - Matching Box Item:", matchingBoxItem);

          if (shipment && matchingBoxItem) {
            console.log("DEBUG - Adding row for box", box.boxNumber, "and item", item.item.asin);
            rows.push([
              selectedPlan.inbound_plan_id.substring(selectedPlan.inbound_plan_id.length - 4),
              dateRequested,
              '', // Date Completed
              dateRequestedTime,
              'Pending',
              clientId,
              box.boxNumber || box.box_number || box.number,
              '',
              'Amazon FBA',
              shipment.destination.address.address_line_1,
              shipment.destination.address.address_line_2 || '',
              shipment.destination.address.address_line_3 || '',
              shipment.destination.address.city,
              shipment.destination.address.state_or_province_code,
              shipment.destination.address.postal_code,
              box.items.find(boxItem => (boxItem.asin || boxItem.ASIN) == item.item.asin)?.quantity || box.items.find(boxItem => (boxItem.asin || boxItem.ASIN) == item.item.asin)?.QTY || matchingBoxItem.quantity || matchingBoxItem.QTY, // Amount
              item.item.asin,
              '',
              2,
              '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
            ]);
          } else {
            console.log("DEBUG - No match found for item in this box");
            console.log("Box items:", box.items);
            console.log("Item:", item);
          }
        });
      });

      console.log('Rows to Append:', rows);

      if (rows.length === 0) {
        console.log("DEBUG - No rows were generated! Checking conditions:");
        console.log("- selectedItems length:", selectedItems.length);
        console.log("- boxes length:", boxes?.length);
        console.log("- confirmedPlacementOption:", confirmedPlacementOption);
        throw new Error('No rows were generated. Please check that items exist in boxes and placement is confirmed.');
      }

      const response = await gapi.client.sheets.spreadsheets.values.append({
        spreadsheetId: SPREADSHEET_ID,
        range: SHEET_NAME,
        valueInputOption: 'RAW',
        insertDataOption: 'INSERT_ROWS',
        resource: {
          values: rows,
        },
      });

      setSubmissionResult({
        success: true,
        count: rows.length,
        sheetUrl: `https://docs.google.com/spreadsheets/d/${SPREADSHEET_ID}`
      });
      setDialogOpen(true);

    } catch (error) {
      console.error('Error submitting FDA data:', error);
      setSubmissionResult({
        success: false,
        error: error.message
      });
      setDialogOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (submissionResult?.success) {
      onComplete();
    }
  };

  const hasExistingEntry = (asin) => uniqueAsins.has(asin);

  // Select All functionality
  const handleSelectAll = () => {
    if (selectedItems.length === shipmentItems.length) {
      // Deselect all
      shipmentItems.forEach(item => {
        if (selectedItems.some(selected => selected.id === item.id)) {
          onItemToggle(item);
        }
      });
    } else {
      // Select all
      shipmentItems.forEach(item => {
        if (!selectedItems.some(selected => selected.id === item.id)) {
          onItemToggle(item);
        }
      });
    }
  };

  const allSelected = shipmentItems.length > 0 && selectedItems.length === shipmentItems.length;
  const someSelected = selectedItems.length > 0 && selectedItems.length < shipmentItems.length;

  // Handle submission based on method
  const handleSubmission = () => {
    if (submissionMethod === 'api') {
      handleApiSubmission();
    } else {
      handleFDASubmission();
    }
  };

  // Refresh submission status with new API
  const refreshSubmissionStatus = async (submission) => {
    try {
      const response = await fetch(`${FDA_API_BASE_URL}${submission.statusEndpoint}`);
      if (response.ok) {
        const statusData = await response.json();
        
        if (!statusData.success) {
          console.error('Status refresh returned error:', statusData.error);
          return;
        }
        
        // Update local state with new format
        setApiSubmissions(prev => prev.map(sub => {
          if (sub.id === submission.id) {
            const updatedSubmissions = statusData.boxes?.map(box => {
              const existingSub = sub.submissions?.find(s => 
                s.boxId === box.boxId || s.box_number === box.boxId?.replace('BOX-', '')
              );
              
              return {
                ...existingSub,
                id: box.boxId,
                boxId: box.boxId,
                box_number: box.boxId?.replace('BOX-', ''),
                status: box.status,
                progress: box.progress,
                hasResult: box.hasResult,
                hasError: box.hasError
              };
            }) || sub.submissions;

            return {
              ...sub,
              status: statusData.status,
              progress: statusData.progress,
              completedSubmissions: statusData.summary?.completedBoxes || 0,
              failedSubmissions: statusData.summary?.failedBoxes || 0,
              updatedAt: statusData.timestamp,
              submissions: updatedSubmissions
            };
          }
          return sub;
        }));

        // Update database
        await updateFDASubmissionInDatabase(submission.id, statusData, statusData.boxes || []);
        
        // If completed, fetch results
        if (statusData.status === 'completed') {
          await fetchJobResults(submission.id);
        }
      }
    } catch (error) {
      console.error('Error refreshing status:', error);
    }
  };

  // Delete submission function
  const deleteSubmission = async (submission) => {
    setSubmissionToDelete(submission);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSubmission = async () => {
    if (!submissionToDelete) return;
    
    setIsDeleting(true);
    try {
      const jobId = submissionToDelete.job_id || submissionToDelete.id;
      
      const response = await fetch(`${API_URL}/api/fda-submissions/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete submission: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Remove from local state
        setApiSubmissions(prev => prev.filter(sub => {
          const subJobId = sub.job_id || sub.id;
          return subJobId !== jobId;
        }));
        
        // Clear any polling intervals for this submission
        setPollingIntervals(prev => {
          const newMap = new Map(prev);
          newMap.delete(jobId);
          return newMap;
        });
        
        console.log(`Successfully deleted FDA submission: ${jobId}`);
      } else {
        throw new Error(result.message || 'Failed to delete submission');
      }
      
    } catch (error) {
      console.error('Error deleting FDA submission:', error);
      alert(`Failed to delete submission: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSubmissionToDelete(null);
    }
  };

  const cancelDeleteSubmission = () => {
    setDeleteDialogOpen(false);
    setSubmissionToDelete(null);
  };

  // ASIN Breakdown functions
  const showBreakdown = async (submission) => {
    console.log('Opening breakdown for submission:', submission);
    setSubmissionForBreakdown(submission);
    setBreakdownDialogOpen(true);
    
    // Always try to fetch detailed info for completed submissions
    if (submission.status === 'completed') {
      await fetchDetailedBreakdown(submission);
    }
  };

  const fetchDetailedBreakdown = async (submission) => {
    setIsLoadingBreakdown(true);
    try {
      const jobId = submission.job_id || submission.id;
      console.log('Fetching detailed breakdown for job:', jobId);
      
      const response = await fetch(`${FDA_API_BASE_URL}/api/fda/jobs/${jobId}/results`);
      
      if (response.ok) {
        const resultsData = await response.json();
        console.log('FDA Results Data:', resultsData);
        
        if (resultsData.success && resultsData.boxes) {
          // Update the submission with detailed product info
          setApiSubmissions(prev => prev.map(sub => {
            if ((sub.job_id || sub.id) === jobId) {
              const updatedSubmissions = resultsData.boxes.map(box => {
                const existingSub = sub.submissions?.find(s => 
                  s.boxId === box.boxId || s.box_number === box.boxId?.replace('BOX-', '')
                );

                console.log('Processing box result:', {
                  boxId: box.boxId,
                  hasResult: !!box.result,
                  products: box.result?.products,
                  manufacturer: box.result?.manufacturer,
                  facility: box.result?.facility
                });

                return {
                  ...existingSub,
                  boxId: box.boxId,
                  box_number: box.boxId?.replace('BOX-', ''),
                  status: box.status,
                  result: {
                    ...existingSub?.result,
                    productInfo: box.result?.products || box.result?.productDetails || existingSub?.result?.productInfo,
                    fdaSubmissionId: box.result?.fdaSubmissionId || box.result?.confirmationNumber,
                    manufacturerInfo: box.result?.manufacturer || box.result?.manufacturerDetails,
                    facilityInfo: box.result?.facility || box.result?.facilityDetails,
                    timing: box.result?.timing,
                    costs: box.result?.costs,
                    documents: box.result?.documents
                  }
                };
              });

              return {
                ...sub,
                submissions: updatedSubmissions
              };
            }
            return sub;
          }));
          
          // Update the breakdown submission with enhanced data
          setSubmissionForBreakdown(prev => {
            if (!prev) return prev;
            const updated = { ...prev };
            updated.submissions = resultsData.boxes.map(box => {
              const existingSub = prev.submissions?.find(s => 
                s.boxId === box.boxId || s.box_number === box.boxId?.replace('BOX-', '')
              );

              return {
                ...existingSub,
                boxId: box.boxId,
                box_number: box.boxId?.replace('BOX-', ''),
                status: box.status,
                result: {
                  ...existingSub?.result,
                  productInfo: box.result?.products || box.result?.productDetails || existingSub?.result?.productInfo,
                  fdaSubmissionId: box.result?.fdaSubmissionId || box.result?.confirmationNumber,
                  manufacturerInfo: box.result?.manufacturer || box.result?.manufacturerDetails,
                  facilityInfo: box.result?.facility || box.result?.facilityDetails,
                  timing: box.result?.timing,
                  costs: box.result?.costs,
                  documents: box.result?.documents
                }
              };
            });
            return updated;
          });
        }
      } else {
        console.error('Failed to fetch results:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching detailed breakdown:', error);
    } finally {
      setIsLoadingBreakdown(false);
    }
  };

  const closeBreakdown = () => {
    setBreakdownDialogOpen(false);
    setSubmissionForBreakdown(null);
    setIsLoadingBreakdown(false);
  };

  return (
    <div className="space-y-3">
      {/* Compact Header */}
      <div className="flex items-center justify-between p-2 bg-white rounded border border-slate-200">
        <div className="flex items-center space-x-2">
          <Utensils className="w-4 h-4 text-orange-600" />
          <div>
            <h3 className="text-xs font-medium text-slate-900">FDA Food Check</h3>
            <p className="text-xs text-slate-500">Select food items requiring FDA processing</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {selectedItems.length > 0 && (
            <Badge variant="secondary" className="text-xs px-2 py-0.5">
              {selectedItems.length} selected
            </Badge>
          )}
          
          {submissionMethod === 'sheets' && (
            isAuthorized ? (
              <Badge variant="default" className="text-xs px-2 py-0.5 bg-green-100 text-green-700 border-green-200">
                ✓ Connected
              </Badge>
            ) : (
              <Button onClick={handleAuthClick} variant="outline" size="sm" className="h-6 text-xs px-2">
                <ExternalLink className="w-3 h-3 mr-1" />
                Connect
              </Button>
            )
          )}
        </div>
      </div>

      {/* Submission Method Selector */}
      <div className="bg-white rounded border border-slate-200 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Settings className="w-4 h-4 text-slate-600" />
            <span className="text-xs font-medium text-slate-900">Submission Method</span>
          </div>
          
          <Select value={submissionMethod} onValueChange={setSubmissionMethod}>
            <SelectTrigger className="w-48 h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="api">
                <div className="flex items-center space-x-2">
                  <FileText className="w-3 h-3" />
                  <span>Direct PDF Generation</span>
                </div>
              </SelectItem>
              <SelectItem value="sheets">
                <div className="flex items-center space-x-2">
                  <ExternalLink className="w-3 h-3" />
                  <span>Google Sheets</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="mt-2 text-xs text-slate-500">
          {submissionMethod === 'api' 
            ? 'Generate FDA prior notice PDFs directly via API' 
            : 'Submit entries to Google Sheets for manual processing'
          }
        </div>
      </div>

      {/* Conditional Alert for Google Sheets */}
      {submissionMethod === 'sheets' && !isAuthorized && (
        <Alert className="bg-orange-50 border-orange-200 py-2">
          <AlertTriangle className="h-3 w-3 text-orange-600" />
          <AlertDescription className="text-xs text-orange-700">
            <strong>Authorization required:</strong> Connect to Google Sheets to submit FDA entries.
          </AlertDescription>
        </Alert>
      )}



              {/* Submitter Info Status for API submissions */}
        {submissionMethod === 'api' && (
        <div className="text-xs p-2 border border-slate-200 rounded bg-slate-50">
          {isLoadingSubmitterInfo ? (
            <div className="text-blue-600 flex items-center">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Loading submitter info...
            </div>
          ) : submitterInfoError ? (
            <div className="text-red-600 bg-red-50 p-2 rounded border border-red-200">
              <strong>⚠️ Error:</strong> {submitterInfoError}
            </div>
          ) : submitterInfo ? (
            <div className="text-green-600 flex items-center">
              <CheckCircle className="w-3 h-3 mr-1" />
              Submitter: {submitterInfo.businessName} ({submitterInfo.firstName} {submitterInfo.lastName})
            </div>
          ) : null}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-200">
        <Button onClick={onComplete} variant="outline" size="sm" className="h-7 text-xs">
          No food items - Skip FDA Check
        </Button>
        
        <Button
          onClick={handleSubmission}
          disabled={
            selectedItems.length === 0 || 
            isSubmitting || 
            (submissionMethod === 'sheets' && !isAuthorized) ||
            (submissionMethod === 'api' && (!submitterInfo || isLoadingSubmitterInfo))
          }
          size="sm"
          className="h-7 text-xs bg-orange-600 hover:bg-orange-700"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
              Submitting...
            </>
          ) : (
            <>
              <FileText className="w-3 h-3 mr-1" />
              {submissionMethod === 'api' ? 'Generate PDFs' : 'Submit to Sheets'} ({selectedItems.length})
            </>
          )}
        </Button>
      </div>

      {/* API Submission History */}
      {submissionMethod === 'api' && (isLoadingSubmissions || apiSubmissions.length > 0) && (
        <div className="bg-white rounded border border-slate-200">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center space-x-2">
              <Clock className="w-3 h-3 text-slate-600" />
              <span className="text-xs font-medium text-slate-900">Prior Notice Submissions</span>
            </div>
            <div className="text-xs text-slate-500">
              {isLoadingSubmissions ? (
                <div className="flex items-center space-x-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : (
                `${apiSubmissions.length} submission${apiSubmissions.length !== 1 ? 's' : ''}`
              )}
            </div>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {isLoadingSubmissions ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
                <p className="text-xs text-slate-500">Loading FDA submissions...</p>
              </div>
            ) : apiSubmissions.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {apiSubmissions.map((submission) => (
                  <div key={submission.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {submission.status === 'completed' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : submission.status === 'failed' ? (
                          <AlertCircle className="w-4 h-4 text-red-600" />
                        ) : submission.status === 'processing' ? (
                          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                        ) : (
                          <Clock className="w-4 h-4 text-orange-600" />
                        )}
                        
                        <div>
                          <p className="text-xs font-medium text-slate-900">
                            Job {submission.job_id ? submission.job_id.split('-').pop() : (submission.id ? submission.id.split('-').pop() : 'N/A')}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(submission.createdAt).toLocaleString()}
                          </p>
                          {submission.estimatedCompletionTime && submission.status !== 'completed' && (
                            <p className="text-xs text-blue-600">
                              ETA: {new Date(submission.estimatedCompletionTime).toLocaleTimeString()}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Badge 
                          variant={
                            submission.status === 'completed' ? 'default' : 
                            submission.status === 'failed' ? 'destructive' : 
                            submission.status === 'processing' ? 'secondary' :
                            'outline'
                          }
                          className={`text-xs px-2 py-0.5 ${
                            submission.status === 'processing' ? 'bg-blue-100 text-blue-700 border-blue-200' : ''
                          }`}
                        >
                          {submission.status === 'queued' ? 'Queued' :
                           submission.status === 'processing' ? 'Processing' :
                           submission.status === 'completed' ? 'Completed' :
                           submission.status === 'failed' ? 'Failed' :
                           submission.status}
                        </Badge>
                        
                        <Button
                          onClick={() => refreshSubmissionStatus(submission)}
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          title="Refresh status"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                        
                        <Button
                          onClick={() => showBreakdown(submission)}
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600"
                          title="View ASIN breakdown"
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        
                        <Button
                          onClick={() => deleteSubmission(submission)}
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                          title="Delete submission"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Enhanced Progress Tracking */}
                    {submission.status !== 'completed' && submission.status !== 'failed' && (
                      <div className="mt-2 space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-600">Progress</span>
                          <span className="text-xs font-medium text-slate-700">
                            {submission.progress || 0}%
                          </span>
                        </div>
                        <Progress value={submission.progress || 0} className="h-2" />
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>
                            {submission.completedSubmissions || 0} of {submission.totalSubmissions} boxes completed
                          </span>
                          {submission.status === 'processing' && (
                            <span className="text-blue-600 animate-pulse">
                              Processing...
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Real-time Status for Active Jobs */}
                    {(submission.status === 'processing' || submission.status === 'queued') && (
                      <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                          <span className="text-xs text-blue-700">
                            {submission.status === 'queued' ? 
                              'Job queued for processing' : 
                              'Processing boxes in background'
                            }
                          </span>
                        </div>
                        {submission.estimatedCompletionTime && (
                          <p className="text-xs text-blue-600 mt-1">
                            Estimated completion: {new Date(submission.estimatedCompletionTime).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Enhanced Submission Details */}
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-slate-50 p-2 rounded">
                        <div className="flex items-center space-x-1">
                          <Package className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-600">Total Boxes:</span>
                        </div>
                        <span className="font-medium text-slate-900">{submission.totalSubmissions}</span>
                      </div>
                      
                      <div className="bg-slate-50 p-2 rounded">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3 text-slate-500" />
                          <span className="text-slate-600">Status:</span>
                        </div>
                        <span className={`font-medium ${
                          submission.status === 'completed' ? 'text-green-700' :
                          submission.status === 'failed' ? 'text-red-700' :
                          submission.status === 'processing' ? 'text-blue-700' :
                          'text-orange-700'
                        }`}>
                          {submission.status === 'queued' ? 'In Queue' :
                           submission.status === 'processing' ? 'Processing' :
                           submission.status === 'completed' ? 'All Complete' :
                           submission.status === 'failed' ? 'Failed' :
                           submission.status}
                        </span>
                      </div>
                      
                      {submission.completedSubmissions > 0 && (
                        <div className="bg-green-50 p-2 rounded">
                          <div className="flex items-center space-x-1">
                            <CheckCircle className="w-3 h-3 text-green-600" />
                            <span className="text-green-700">Completed:</span>
                          </div>
                          <span className="font-medium text-green-800">{submission.completedSubmissions}</span>
                        </div>
                      )}
                      
                      {submission.failedSubmissions > 0 && (
                        <div className="bg-red-50 p-2 rounded">
                          <div className="flex items-center space-x-1">
                            <AlertCircle className="w-3 h-3 text-red-600" />
                            <span className="text-red-700">Failed:</span>
                          </div>
                          <span className="font-medium text-red-800">{submission.failedSubmissions}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Download Links */}
                    {submission.status === 'completed' && (submission.submissions || submission.individual_submissions) && (
                      <div className="mt-3 space-y-2">
                        {(() => {
                          // Check for missing boxes in sequence
                          const submissionData = submission.submissions || submission.individual_submissions || [];
                          
                          // Get all submissions regardless of PDF status for missing box detection
                          const allSubmissions = submissionData
                            .filter(sub => sub.status === 'completed') // Only check completed ones
                            .sort((a, b) => (a.box_number || a.boxNumber || 1) - (b.box_number || b.boxNumber || 1));
                          
                          // Get submissions that have PDFs available for download
                          const completedSubmissions = submissionData
                            .filter(sub => sub.status === 'completed' && (sub.result?.pdfBase64 || sub.result?.pdfStatus === 'available' || sub.hasResult))
                            .sort((a, b) => (a.box_number || a.boxNumber || 1) - (b.box_number || b.boxNumber || 1));
                          
                          // Check for missing boxes based on all completed submissions, not just ones with PDFs
                          const allBoxNumbers = allSubmissions.map(sub => {
                            const boxNum = sub.box_number || sub.boxNumber || 1;
                            // Convert string box numbers to integers if needed
                            return typeof boxNum === 'string' ? parseInt(boxNum, 10) : boxNum;
                          }).filter(num => !isNaN(num));
                          
                          console.log('Box number debug:', {
                            jobId: submission.id,
                            totalSubmissions: submissionData.length,
                            completedSubmissions: allSubmissions.length,
                            allBoxNumbers: allBoxNumbers,
                            sampleSubmission: allSubmissions[0] ? {
                              id: allSubmissions[0].id,
                              box_number: allSubmissions[0].box_number,
                              boxNumber: allSubmissions[0].boxNumber,
                              status: allSubmissions[0].status
                            } : 'none'
                          });
                          
                          const missingBoxes = [];
                          
                          if (allBoxNumbers.length > 0) {
                            const minBox = Math.min(...allBoxNumbers);
                            const maxBox = Math.max(...allBoxNumbers);
                            
                            for (let i = minBox; i <= maxBox; i++) {
                              if (!allBoxNumbers.includes(i)) {
                                missingBoxes.push(i);
                              }
                            }
                          }
                          
                          return (
                            <>
                              {/* Missing Box Warning */}
                              {missingBoxes.length > 0 && allBoxNumbers.length > 0 && (
                                <Alert className="bg-red-50 border-red-200 py-2">
                                  <AlertTriangle className="h-3 w-3 text-red-600" />
                                  <AlertDescription className="text-xs text-red-700">
                                    <strong>⚠️ Missing Boxes Detected:</strong> Box{missingBoxes.length > 1 ? 'es' : ''} {missingBoxes.join(', ')} {missingBoxes.length > 1 ? 'are' : 'is'} missing from the sequence ({Math.min(...allBoxNumbers)}-{Math.max(...allBoxNumbers)}). You might need to retry the prior notices for the missing box{missingBoxes.length > 1 ? 'es' : ''}.
                                  </AlertDescription>
                                </Alert>
                              )}
                              
                              {/* Individual PDF Downloads */}
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <FileText className="w-4 h-4 text-slate-600" />
                                  <span className="text-sm font-medium text-slate-900">Individual PDFs</span>
                                  <Badge variant="outline" className="text-xs">
                                    {completedSubmissions.length} ready
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {completedSubmissions.map((sub, index) => {
                                    const boxNumber = sub.box_number || sub.boxNumber || (index + 1);
                                    const hasValidPdf = sub.result?.pdfBase64 || sub.result?.pdfStatus === 'available';
                                    
                                    return (
                                      <Button
                                        key={index}
                                        onClick={() => downloadPDF(sub)}
                                        variant={hasValidPdf ? "outline" : "ghost"}
                                        size="sm"
                                        className={`h-6 text-xs ${
                                          hasValidPdf ? 
                                          'border-green-200 text-green-700 hover:bg-green-50' : 
                                          'text-slate-400 cursor-not-allowed'
                                        }`}
                                        disabled={!hasValidPdf}
                                        title={hasValidPdf ? 'Download PDF' : 'PDF not yet available'}
                                      >
                                        <Download className="w-3 h-3 mr-1" />
                                        Box {boxNumber}
                                        {sub.asin && sub.asin !== 'Multiple Products' && (
                                          <span className="ml-1 text-xs opacity-70">({sub.asin.slice(-4)})</span>
                                        )}
                                      </Button>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          );
                        })()}
                        
                        {/* Enhanced Merged PDF Download */}
                        {(() => {
                           const submissionData = submission.submissions || submission.individual_submissions || [];
                           const completedSubmissions = submissionData.filter(sub => sub.status === 'completed' && (sub.result?.pdfBase64 || sub.result?.pdfStatus === 'available'));
                           const completedCount = completedSubmissions.length;
                           const jobId = submission.job_id || submission.id;
                           const isProcessing = processingPdf === jobId;
                           
                           // Get box number range for display
                           const boxNumbers = completedSubmissions.map(sub => sub.box_number || sub.boxNumber || 1).sort((a, b) => a - b);
                           const minBox = boxNumbers[0];
                           const maxBox = boxNumbers[boxNumbers.length - 1];
                           const rangeText = minBox === maxBox ? `Box ${minBox}` : `Boxes ${minBox}-${maxBox}`;
                           
                           return completedCount > 1 ? (
                             <div className="space-y-2">
                               <div className="flex items-center space-x-2">
                                 <FileStack className="w-4 h-4 text-slate-600" />
                                 <span className="text-sm font-medium text-slate-900">Merged Downloads</span>
                                 <Badge variant="secondary" className="text-xs">
                                   {completedCount} boxes
                                 </Badge>
                               </div>
                               
                               <div className="grid grid-cols-1 gap-2">
                                 <Button
                                   onClick={() => downloadMergedStampedPDF(submission)}
                                   variant="default"
                                   size="sm"
                                   className="h-8 text-xs bg-orange-600 hover:bg-orange-700 justify-start"
                                   disabled={isProcessing}
                                 >
                                   {isProcessing ? (
                                     <>
                                       <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                       Processing...
                                     </>
                                   ) : (
                                     <>
                                       <FileStack className="w-3 h-3 mr-2" />
                                       Standard Merged PDF ({rangeText})
                                     </>
                                   )}
                                 </Button>
                                 
                                 <Button
                                   onClick={() => downloadMergedStamped4x6PDF(submission)}
                                   variant="default"
                                   size="sm"
                                   className="h-8 text-xs bg-purple-600 hover:bg-purple-700 justify-start"
                                   disabled={isProcessing}
                                 >
                                   {isProcessing ? (
                                     <>
                                       <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                                       Converting to 4x6...
                                     </>
                                   ) : (
                                     <>
                                       <FileStack className="w-3 h-3 mr-2" />
                                       4x6 Label Format ({rangeText})
                                     </>
                                   )}
                                 </Button>
                               </div>
                               
                               <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                                 💡 <strong>Tip:</strong> Standard format for documents, 4x6 format for thermal label printers
                               </div>
                             </div>
                           ) : null;
                         })()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Clock className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                <p className="text-xs text-slate-500">No FDA submissions yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Extended Items List */}
      <div className="bg-white rounded border border-slate-200">
        {/* Minimal List Header with Select All */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSelectAll}
              className="text-xs font-medium text-slate-700 hover:text-slate-900"
            >
              {allSelected ? 'Deselect All' : 'Select All'} ({shipmentItems.length} items)
            </button>
          </div>
          
          <div className="text-xs text-slate-500">
            Manual verification required
          </div>
        </div>

        {/* Extended Compact Items */}
        <div className="max-h-96 overflow-y-auto">
          {shipmentItems.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {shipmentItems.map((item, index) => {
                const isSelected = selectedItems.some(selected => selected.id === item.id);
                const hasEntry = hasExistingEntry(item.item.asin);
                
                return (
                  <div 
                    key={item.id} 
                    className={`flex items-center space-x-3 p-3 cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'bg-orange-100 border-l-4 border-orange-500 hover:bg-orange-200' 
                        : 'bg-white hover:bg-slate-50 border-l-4 border-transparent'
                    }`}
                    onClick={() => onItemToggle(item)}
                  >
                    <img 
                      src={
                        (item.item.images && item.item.images.length > 0 && item.item.images[0].large_url) || 
                        'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq'
                      }
                      alt={item.item.title} 
                      className="w-10 h-10 object-cover rounded border border-slate-200 flex-shrink-0"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className={`w-10 h-10 bg-slate-100 rounded border border-slate-200 flex items-center justify-center flex-shrink-0 ${item.item.images && item.item.images.length > 0 ? 'hidden' : 'flex'}`}>
                      <Package className="w-5 h-5 text-slate-400" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        isSelected ? 'text-slate-900' : 'text-slate-700'
                      }`}>
                        {item.item.title}
                      </p>
                      <div className={`flex items-center space-x-3 text-xs mt-0.5 ${
                        isSelected ? 'text-slate-600' : 'text-slate-500'
                      }`}>
                        <span>ASIN: {item.item.asin}</span>
                        {item.item.merchant_sku && (
                          <>
                            <span>•</span>
                            <span>SKU: {item.item.merchant_sku}</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      {isSelected && (
                        <Badge variant="default" className="text-xs px-2 py-0.5 bg-orange-600 text-white">
                          Selected
                        </Badge>
                      )}
                      {hasEntry && (
                        <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                          Processed
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Utensils className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No items in shipment</p>
            </div>
          )}
        </div>
      </div>

      {/* Success/Error Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-base">
              {submissionResult?.success ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>FDA Submission Successful</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span>FDA Submission Failed</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-3">
            {submissionResult?.success ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  {submissionResult.method === 'api' 
                    ? `Successfully created FDA API job with ${submissionResult.count} submissions.`
                    : `Successfully submitted ${submissionResult.count} FDA entries.`
                  }
                </p>
                <div className="bg-green-50 p-3 rounded border border-green-200">
                  {submissionResult.method === 'api' ? (
                    <div className="space-y-2">
                      <p className="text-xs text-green-700">
                        <strong>Job ID:</strong> {submissionResult.jobId}
                      </p>
                      <p className="text-xs text-green-700">
                        PDFs will be generated automatically. Check the submission history above for progress and download links.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-green-700">
                      <a 
                        href={submissionResult.sheetUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline hover:text-green-800"
                      >
                        View in Google Sheets ↗
                      </a>
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Failed to submit FDA entries{submissionResult?.method === 'api' ? ' via API' : ''}:
                </p>
                <div className="bg-red-50 p-3 rounded border border-red-200">
                  <p className="text-xs text-red-700">
                    {submissionResult?.error || 'Unknown error occurred'}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={handleDialogClose} size="sm" className="h-8 text-xs">
              {submissionResult?.success ? 'Continue' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-base">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span>Delete FDA Submission</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-3">
            <p className="text-sm text-slate-600 mb-3">
              Are you sure you want to delete this FDA submission batch? This action cannot be undone.
            </p>
            
            {submissionToDelete && (
              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Job ID:</span>
                    <span className="font-medium text-slate-900">
                      {submissionToDelete.job_id ? 
                        submissionToDelete.job_id.split('-').pop() : 
                        (submissionToDelete.id ? submissionToDelete.id.split('-').pop() : 'N/A')
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Status:</span>
                    <span className={`font-medium ${
                      submissionToDelete.status === 'completed' ? 'text-green-700' :
                      submissionToDelete.status === 'failed' ? 'text-red-700' :
                      'text-orange-700'
                    }`}>
                      {submissionToDelete.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Created:</span>
                    <span className="font-medium text-slate-900">
                      {new Date(submissionToDelete.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Boxes:</span>
                    <span className="font-medium text-slate-900">
                      {submissionToDelete.totalSubmissions}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-3 text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
              <strong>⚠️ Warning:</strong> This will permanently delete all submission data, including PDFs and processing history.
            </div>
          </div>
          
          <DialogFooter className="flex justify-between">
            <Button 
              onClick={cancelDeleteSubmission} 
              variant="outline" 
              size="sm" 
              className="h-8 text-xs"
              disabled={isDeleting}
            >
              <X className="w-3 h-3 mr-1" />
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteSubmission} 
              variant="destructive" 
              size="sm" 
              className="h-8 text-xs"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ASIN Breakdown Dialog */}
      <Dialog open={breakdownDialogOpen} onOpenChange={setBreakdownDialogOpen}>
        <DialogContent className="w-[70vw] h-[70vh] overflow-hidden flex flex-col" style={{width: '70vw', height: '70vh', maxWidth: '70vw', maxHeight: '70vh'}}>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-lg">
              <Tag className="w-5 h-5 text-blue-600" />
              <span>ASIN Breakdown</span>
              {submissionForBreakdown && (
                <Badge variant="outline" className="text-xs">
                  Job {submissionForBreakdown.job_id ? 
                    submissionForBreakdown.job_id.split('-').pop() : 
                    (submissionForBreakdown.id ? submissionForBreakdown.id.split('-').pop() : 'N/A')
                  }
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {isLoadingBreakdown ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin mr-2 text-blue-600" />
                <span className="text-sm text-slate-600">Loading detailed breakdown...</span>
              </div>
            ) : submissionForBreakdown ? (
              <div className="space-y-3">
                {/* Compact Job Summary */}
                <div className="bg-slate-50 p-2 rounded border border-slate-200">
                  <div className="flex items-center space-x-6 text-xs">
                    <div className="flex items-center space-x-2">
                      <span className="text-slate-600">Status:</span>
                      <Badge variant={
                        submissionForBreakdown.status === 'completed' ? 'default' :
                        submissionForBreakdown.status === 'failed' ? 'destructive' :
                        'secondary'
                      } className="text-xs px-2 py-0.5">
                        {submissionForBreakdown.status}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-slate-600">Boxes:</span>
                      <span className="font-medium text-slate-900">{submissionForBreakdown.totalSubmissions}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-slate-600">Completed:</span>
                      <span className="font-medium text-green-700">{submissionForBreakdown.completedSubmissions || 0}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-slate-600">Created:</span>
                      <span className="font-medium text-slate-900">{new Date(submissionForBreakdown.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {/* ASIN-Focused Breakdown */}
                <div>
                  <h3 className="text-sm font-medium text-slate-900 flex items-center mb-2">
                    <Tag className="w-4 h-4 mr-2 text-slate-600" />
                    ASIN Breakdown
                  </h3>
                  
                  {(() => {
                    // Collect all unique ASINs with their info
                    const asinMap = new Map();
                    const submissionData = submissionForBreakdown.submissions || submissionForBreakdown.individual_submissions || [];
                    
                    console.log('Processing ASIN breakdown data:', {
                      submissionDataLength: submissionData.length,
                      sampleSubmission: submissionData[0],
                      submissionForBreakdown: submissionForBreakdown
                    });
                    
                    submissionData.forEach((submission, index) => {
                      const boxNumber = submission.box_number || submission.boxNumber || (index + 1);
                      const productInfo = submission.result?.productInfo || [];
                      
                      console.log(`Processing submission ${index}:`, {
                        boxNumber,
                        productInfoLength: productInfo.length,
                        submissionAsin: submission.asin,
                        hasResult: !!submission.result,
                        productInfo: productInfo
                      });
                      
                      if (productInfo.length > 0) {
                        productInfo.forEach(product => {
                          const asin = product.asin || submission.asin;
                          if (asin) {
                            if (!asinMap.has(asin)) {
                              // Get manufacturer details including full address
                              const mfrInfo = product.manufacturer || product.manufacturerInfo || product.facility || {};
                              const manufacturerData = typeof mfrInfo === 'string' ? { name: mfrInfo } : mfrInfo;
                              
                              // Get product code with description - check multiple possible fields
                              const productCodeInfo = product.fdaProductCode || product.productCode || product.fdaCode || product.foodCode || product.code;
                              const productCodeDesc = product.fdaProductCodeDescription || product.productCodeDescription || product.foodCodeDescription || product.codeDescription || product.productTypeDescription || product.categoryDescription;
                              
                              console.log('Product code debug:', {
                                asin,
                                productCodeInfo,
                                productCodeDesc,
                                allProductFields: Object.keys(product),
                                fullProduct: product
                              });
                              
                              asinMap.set(asin, {
                                asin,
                                title: product.title || product.name || product.productTitle || product.productName || 'Unknown Product',
                                image: product.images?.[0] || product.image || product.productImage || product.thumbnail,
                                fdaProductCode: productCodeInfo,
                                fdaProductCodeDescription: productCodeDesc,
                                manufacturer: {
                                  name: manufacturerData.name || manufacturerData.companyName || manufacturerData.facilityName,
                                  address: manufacturerData.address || manufacturerData.streetAddress,
                                  city: manufacturerData.city,
                                  state: manufacturerData.state || manufacturerData.stateProvince,
                                  country: manufacturerData.country || manufacturerData.countryCode,
                                  postalCode: manufacturerData.postalCode || manufacturerData.zipCode,
                                  fullAddress: manufacturerData.fullAddress
                                },
                                description: product.description || product.productDescription || product.ingredientStatement,
                                boxes: [],
                                totalQuantity: 0
                              });
                            }
                            asinMap.get(asin).boxes.push({
                              boxNumber,
                              quantity: product.quantity || submission.quantity || 0,
                              status: submission.status,
                              fdaSubmissionId: submission.result?.fdaSubmissionId
                            });
                            asinMap.get(asin).totalQuantity += (product.quantity || submission.quantity || 0);
                          }
                        });
                      } else {
                        // For submissions without detailed product info, try to get ASIN from original submission data
                        const asin = submission.asin || submission.id;
                        if (asin && asin !== 'Multiple Products' && asin !== 'Single Product') {
                          if (!asinMap.has(asin)) {
                            // Try to get product info from the original selected items and shipment data
                            let productTitle = 'Product details pending';
                            let productImage = null;
                            
                            // Check if we can find this in the original submission data or selectedItems
                            try {
                              // First check original submissions
                              const originalSubmissions = submissionForBreakdown.submissions || [];
                              const originalData = originalSubmissions.find(s => s.asin === asin);
                              if (originalData) {
                                productTitle = originalData.title || productTitle;
                                productImage = originalData.image || productImage;
                              }
                              
                              // Also try to get from shipment items if available
                              if (selectedItems && selectedItems.length > 0) {
                                const matchingItem = selectedItems.find(item => item.item?.asin === asin);
                                if (matchingItem) {
                                  productTitle = matchingItem.item.title || matchingItem.item.TITLE || productTitle;
                                  productImage = matchingItem.item.images?.[0]?.large_url || matchingItem.item.images?.[0] || productImage;
                                }
                              }
                            } catch (e) {
                              console.log('Could not find original product data');
                            }
                            
                            asinMap.set(asin, {
                              asin,
                              title: productTitle,
                              image: productImage,
                              fdaProductCode: null,
                              manufacturer: null,
                              description: null,
                              boxes: [],
                              totalQuantity: 0
                            });
                          }
                          asinMap.get(asin).boxes.push({
                            boxNumber,
                            quantity: submission.quantity || 0,
                            status: submission.status,
                            fdaSubmissionId: submission.result?.fdaSubmissionId
                          });
                          asinMap.get(asin).totalQuantity += (submission.quantity || 0);
                        }
                      }
                    });
                    
                    const asins = Array.from(asinMap.values());
                    
                    console.log('Final ASIN data:', {
                      asinCount: asins.length,
                      asins: asins
                    });
                    
                    if (asins.length === 0) {
                      return (
                        <div className="text-center py-6 text-sm text-slate-500">
                          <Tag className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                          <div>No ASIN data available yet</div>
                          <div className="text-xs text-slate-400 mt-1">Product details will appear when processing completes</div>
                          <div className="text-xs text-red-400 mt-2">Debug: {submissionData.length} submissions found</div>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="grid grid-cols-1 gap-2">
                        {asins.map((asinData, index) => (
                          <div key={index} className="border border-slate-200 rounded p-3 bg-white hover:bg-slate-50 transition-colors">
                            <div className="flex items-start space-x-4">
                              {/* Product Image */}
                              <div className="flex-shrink-0">
                                <div className="w-20 h-20 border border-slate-200 rounded overflow-hidden bg-white">
                                  {asinData.image ? (
                                    <img 
                                      src={asinData.image}
                                      alt={asinData.title}
                                      className="w-full h-full object-contain"
                                      onError={(e) => {
                                        console.log('Image failed to load:', asinData.image);
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                      onLoad={() => {
                                        console.log('Image loaded successfully:', asinData.image);
                                      }}
                                    />
                                  ) : null}
                                  <div className={`w-full h-full bg-slate-100 flex items-center justify-center ${
                                    asinData.image ? 'hidden' : 'flex'
                                  }`}>
                                    <div className="text-center">
                                      <Package className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                                      <div className="text-xs text-slate-500">No Image</div>
                                    </div>
                                  </div>
                                </div>
                                {/* Debug info */}
                                {asinData.image && (
                                  <div className="text-xs text-slate-400 mt-1 truncate max-w-[80px]" title={asinData.image}>
                                    {asinData.image.split('/').pop()}
                                  </div>
                                )}
                              </div>
                              
                              {/* Product Details */}
                              <div className="flex-1 min-w-0">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                  {/* Basic Product Info */}
                                  <div className="lg:col-span-2">
                                    <h4 className="text-base font-semibold text-slate-900 mb-1">
                                      {asinData.title}
                                    </h4>
                                    <div className="flex items-center space-x-4 text-sm text-slate-600 mb-2">
                                      <div className="flex items-center space-x-1">
                                        <Tag className="w-4 h-4" />
                                        <span>ASIN:</span>
                                        <span className="font-mono font-medium text-slate-900">{asinData.asin}</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Hash className="w-4 h-4" />
                                        <span>Total:</span>
                                        <span className="font-medium text-slate-900">{asinData.totalQuantity} units</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Package className="w-4 h-4" />
                                        <span>{asinData.boxes.length} box{asinData.boxes.length !== 1 ? 'es' : ''}</span>
                                      </div>
                                    </div>
                                    
                                    {/* Box Distribution */}
                                    <div className="flex items-center space-x-1 text-sm">
                                      <span className="text-slate-600">Boxes:</span>
                                      <div className="flex flex-wrap gap-1">
                                        {asinData.boxes.map((box, boxIndex) => (
                                          <Badge key={boxIndex} variant="outline" className="text-xs">
                                            Box {box.boxNumber} ({box.quantity})
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* FDA Information */}
                                  <div className="lg:col-span-1">
                                    {(asinData.fdaProductCode || asinData.manufacturer?.name || asinData.description) ? (
                                      <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                        <h5 className="text-sm font-medium text-blue-900 mb-3 flex items-center">
                                          <FileText className="w-4 h-4 mr-1" />
                                          FDA Details
                                        </h5>
                                        <div className="space-y-3 text-sm">
                                          {asinData.fdaProductCode && (
                                            <div>
                                              <span className="text-blue-700 font-medium flex items-center mb-1">
                                                <Hash className="w-3 h-3 mr-1" />
                                                FDA Product Code:
                                              </span>
                                              <div className="font-mono text-blue-900 font-semibold">{asinData.fdaProductCode}</div>
                                              {asinData.fdaProductCodeDescription && (
                                                <div className="text-blue-800 text-xs mt-1 bg-blue-100 p-2 rounded">
                                                  {asinData.fdaProductCodeDescription}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          
                                          {asinData.manufacturer?.name && (
                                            <div>
                                              <span className="text-blue-700 font-medium flex items-center mb-1">
                                                <Building className="w-3 h-3 mr-1" />
                                                Manufacturer:
                                              </span>
                                              <div className="text-blue-900 font-medium">{asinData.manufacturer.name}</div>
                                              {(asinData.manufacturer.address || asinData.manufacturer.city || asinData.manufacturer.fullAddress) && (
                                                <div className="text-blue-800 text-xs mt-1 space-y-0.5">
                                                  {asinData.manufacturer.fullAddress ? (
                                                    <div>{asinData.manufacturer.fullAddress}</div>
                                                  ) : (
                                                    <>
                                                      {asinData.manufacturer.address && (
                                                        <div>{asinData.manufacturer.address}</div>
                                                      )}
                                                      {(asinData.manufacturer.city || asinData.manufacturer.state) && (
                                                        <div>
                                                          {asinData.manufacturer.city}{asinData.manufacturer.city && asinData.manufacturer.state ? ', ' : ''}{asinData.manufacturer.state} {asinData.manufacturer.postalCode}
                                                        </div>
                                                      )}
                                                      {asinData.manufacturer.country && (
                                                        <div>{asinData.manufacturer.country}</div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                          
                                          {asinData.description && (
                                            <div>
                                              <span className="text-blue-700 font-medium flex items-center mb-1">
                                                <FileText className="w-3 h-3 mr-1" />
                                                Product Description:
                                              </span>
                                              <div className="text-blue-900 text-xs leading-relaxed bg-blue-100 p-2 rounded">
                                                {asinData.description}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="bg-slate-50 p-3 rounded border border-slate-200 text-center">
                                        <Clock className="w-6 h-6 mx-auto mb-1 text-slate-400" />
                                        <div className="text-xs text-slate-500">FDA details pending</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileBarChart className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p className="text-sm text-slate-500">No submission data available</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={closeBreakdown} size="sm" className="h-8 text-xs">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FDASection; 